import { ReactNode, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import s from "./Fab.module.scss";

/**
 * The phone's floating main action (bottom end). Extended shows icon + words; compact only the round icon, with the
 * same 56 px tap area and the same name (`aria-label` = `label` in both states). A link with `to`, else a button.
 * `shrinkOnScroll`: compact while the page scrolls down, extended on scrolling up or after a pause (the Fab
 * re-renders, not the page). Otherwise `extended` decides. The page keeps room under its last item for it.
 */
export const Fab = ({ label, icon, extended: fixed = true, shrinkOnScroll = false, to, onClick, className }:
  { label: string; icon: ReactNode; extended?: boolean; shrinkOnScroll?: boolean; to?: string; onClick?: () => void; className?: string }) => {
  const dir = useScrollDirection({ enabled: shrinkOnScroll });
  const extended = shrinkOnScroll ? dir !== "down" : fixed;
  const cls = [s.fab, extended ? "" : s.compact, className ?? ""].filter(Boolean).join(" ");
  const body = <><span aria-hidden="true" className={s.icon}>{icon}</span><span aria-hidden="true" className={s.text}>{label}</span></>;
  const common = { className: cls, "aria-label": label, "data-extended": String(extended) };
  return to
    ? <Link to={to} {...common} onClick={onClick}>{body}</Link>
    : <button type="button" {...common} onClick={onClick}>{body}</button>;
};

export type ScrollDirection = "down" | "up" | null;

/**
 * Which way the page is being scrolled: "down", "up", or null once it has been still for `idleMs` (and at first).
 * Movements under `threshold` px are ignored; read once per animation frame. Listens to the window (the new frame scrolls the window), passive.
 * Used by `Fab shrinkOnScroll`; call it directly for another floating piece.
 */
export const useScrollDirection = ({ idleMs = 600, threshold = 4, enabled = true }: { idleMs?: number; threshold?: number; enabled?: boolean } = {}): ScrollDirection => {
  const [dir, setDir] = useState<ScrollDirection>(null);
  const last = useRef(0);
  useEffect(() => {
    if (!enabled) { setDir(null); return undefined; }
    last.current = window.scrollY;
    let timer: number | undefined;
    let frame = 0;
    // At most one reading per frame (scroll events can fire many times a frame).
    const read = () => {
      frame = 0;
      const y = window.scrollY;
      const delta = y - last.current;
      if (Math.abs(delta) < threshold) return;
      last.current = y;
      // At the very top the words always show.
      setDir(y <= 0 ? null : delta > 0 ? "down" : "up");
      window.clearTimeout(timer);
      timer = window.setTimeout(() => setDir(null), idleMs);
    };
    const onScroll = () => { if (!frame) frame = window.requestAnimationFrame(read); };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => { window.removeEventListener("scroll", onScroll); window.clearTimeout(timer); if (frame) window.cancelAnimationFrame(frame); };
  }, [idleMs, threshold, enabled]);
  return dir;
};
